/**
 * PudoLocationCapture - Capture and update PUDO location with GPS
 */
import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { MapPin, Navigation, Loader2, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const PudoLocationCapture = ({ businessId, onLocationUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [formData, setFormData] = useState({
    street: "",
    city: "",
    parish: "",
    latitude: null,
    longitude: null,
    hours: "Mon-Sat 9:00 AM - 6:00 PM"
  });

  useEffect(() => {
    fetchLocation();
  }, [businessId]);

  const fetchLocation = async () => {
    try {
      const response = await api.get("/business/pudo/my-location");
      if (response.data) {
        const addr = response.data.address || {};
        const coords = response.data.gps_coordinates || {};
        setFormData({
          street: addr.street || "",
          city: addr.city || response.data.city || "",
          parish: addr.parish || response.data.parish || "",
          latitude: coords.latitude || response.data.latitude,
          longitude: coords.longitude || response.data.longitude,
          hours: response.data.hours || "Mon-Sat 9:00 AM - 6:00 PM"
        });
      }
    } catch (error) {
      console.log("No location found");
    }
  };

  const captureCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    setCapturing(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }));
        toast.success("Location captured successfully!");
        setCapturing(false);
      },
      (error) => {
        toast.error("Failed to capture location: " + error.message);
        setCapturing(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const saveLocation = async () => {
    if (!formData.street || !formData.latitude || !formData.longitude) {
      toast.error("Please capture your location and enter street address");
      return;
    }
    setLoading(true);
    try {
      await api.post("/business/pudo/update-location", {
        address: formData.street,
        city: formData.city,
        parish: formData.parish,
        latitude: formData.latitude,
        longitude: formData.longitude,
        hours: formData.hours
      });
      toast.success("Location saved! Drivers can now find you.");
      onLocationUpdated?.();
      fetchLocation();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save location");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <MapPin className="h-5 w-5 text-teal-400" />
          PUDO Location
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Button
            onClick={captureCurrentLocation}
            disabled={capturing}
            className="bg-teal-600 hover:bg-teal-700"
          >
            {capturing ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Capturing...</>
            ) : (
              <><Navigation className="h-4 w-4 mr-2" />Capture Current Location</>
            )}
          </Button>
          {formData.latitude && formData.longitude && (
            <span className="text-sm text-teal-400 flex items-center gap-1">
              <Check className="h-4 w-4" />
              {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label className="text-slate-300">Street Address *</Label>
            <Input
              value={formData.street}
              onChange={(e) => setFormData({ ...formData, street: e.target.value })}
              placeholder="123 Main Street, Kingston"
              className="bg-slate-800 border-slate-700 text-white mt-1"
            />
          </div>
          <div>
            <Label className="text-slate-300">City</Label>
            <Input
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              placeholder="Kingston"
              className="bg-slate-800 border-slate-700 text-white mt-1"
            />
          </div>
          <div>
            <Label className="text-slate-300">Parish</Label>
            <Input
              value={formData.parish}
              onChange={(e) => setFormData({ ...formData, parish: e.target.value })}
              placeholder="St. Andrew"
              className="bg-slate-800 border-slate-700 text-white mt-1"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-slate-300">Operating Hours</Label>
            <Input
              value={formData.hours}
              onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
              placeholder="Mon-Sat 9:00 AM - 6:00 PM"
              className="bg-slate-800 border-slate-700 text-white mt-1"
            />
          </div>
        </div>
        {!formData.latitude && (
          <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            Please capture your location so drivers can find you
          </div>
        )}
        <Button
          onClick={saveLocation}
          disabled={loading || !formData.latitude}
          className="w-full bg-primary"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Save Location
        </Button>
      </CardContent>
    </Card>
  );
};

export default PudoLocationCapture;
